import queryString from 'query-string';

// Shared utilities
import {
  AppLogger,
  AuthClient,
  AuthEvents,
  DatadogClient,
} from '@workivate/tho-web-shared';
import GoogleAnalytics from 'services/google-analytics/analytics';

// Globals
import * as cookieManager from 'globals/cookieManager';
import { getRegionalApiEndpoints, lookupKeys } from 'globals/regional-api';
import { testableFeaturesEnabled, setTestableFlag } from 'globals/testable';
import waFetch from 'globals/wa-fetch';
import {
  storageKeys,
  getPersistence,
  isLocalStorageSupported,
} from 'globals/storage';

import { initiliaseWondering } from 'globals/wondering/helpers';
import { calculateResponsiveState } from 'redux-responsive';

import Language from 'language';
import { initialiseLogin } from 'globals/fetch.helpers';

// App-specific utilities
import {
  getMsTeamsUrl,
  setSubDomainCookie,
  handleSubdomainRedirect,
  setJwtCookie,
  getSubdomainCookie,
} from 'app/state/session/helpers';
import { runOnWebWorker } from 'app/run-on-web-worker';
import { getStore, initializeStore } from 'app/hermes-redux';

// Components
import { showToast } from 'react-components/toaster-comp/state/actions';
import { ToastType } from 'react-components/toaster-comp/state/types';
import { getStoredSelectedEnvironment } from 'react-components/env-controller/env-controller-context';

// State management
import { createSubscription } from 'hermes-redux.helpers';
import rootSagas from './state/sagas';
import { getSubdomainFromHost, getSuffixFromHref } from 'router/navigation';
import * as CompanyActions from 'stores/company-store';
import { appLogger } from '@workivate/tho-web-shared/src/appLogger/appLogger';
import isEmpty from 'lodash/isEmpty';
import { getRootHost } from 'globals/helpers';
import { renderHermes } from 'run';

const container = document.getElementById('react-view');

if (!container) {
  throw new Error('Could not find the react container');
}
// --------------------------------------------------------------------------------------------
// Read URL parameters
//
const urlParams = queryString.parse(location.search, {
  parseBooleans: true,
});

const isOnLocalhost = window.origin.includes('localhost');

const uslOTT: string = urlParams['x-usl-ott'];
const tenancyHashParam: string =
  urlParams['tenancy_hash'] || urlParams['tenancyHash'];

// These parameters are only set when we are running the project locally
// due to restrictions on setting cookies through localhost
// localhost subdomains are treated as different domains and don't allow cookies to be shared
const refreshTokenParam = urlParams['refreshToken'];
const clientIdParam = urlParams['clientId'];
const hasUrlParams = !isEmpty(urlParams);
const jwtCookie = JSON.parse(cookieManager.getCookie('JWT_COOKIE') as string);
const stayOnGenericSubdomain =
  isOnLocalhost &&
  localStorage.getItem(storageKeys.stayOnGenericSubdomain) === 'true';

const currentDomain = '.' + getRootHost();
const isOnGenericSubdomain =
  getSubdomainFromHost() === 'app' || getSubdomainFromHost() === 'login';

const uslOttPresent = !!uslOTT;
const auth = JSON.parse(cookieManager.getCookie('WAM_AUTH') as string);
const userToken =
  (hasUrlParams && urlParams['userToken']) || (!isEmpty(auth) && auth.wamToken);

const extractDomain = url => {
  try {
    const formattedUrl = url.startsWith('http') ? url : `http://${url}`;
    const parsedUrl = new URL(formattedUrl);

    return parsedUrl.host; // Includes hostname and port (e.g., "localhost:3000")
  } catch (error) {
    console.error('Invalid URL:', error);
    return '';
  }
};

if (
  isOnGenericSubdomain &&
  isOnLocalhost &&
  isEmpty(localStorage.getItem(storageKeys.stayOnGenericSubdomain))
) {
  localStorage.setItem(storageKeys.stayOnGenericSubdomain, 'true');
}

/**
 *
 * @param companySubdomain
 * @param isPostSignup
 * @param path
 * @param isOauth2
 */
export const handleLoginRedirect = async (companySubdomain: string) => {
  // Get everything after the root domain name eg https://app.localhost:3000/?code=56e returns .localhost:3000/?code=56e
  const suffix = getSuffixFromHref();
  const rootDomain = extractDomain(suffix);
  const persistence = getPersistence();
  const msTeamsUrl = getMsTeamsUrl();
  const storedEnvironment = getStoredSelectedEnvironment();

  await setJwtCookie({
    refreshToken: AuthClient.refreshToken,
    clientId: AuthClient.clientId,
    tenancyHash: localStorage.getItem(lookupKeys.tenancyHash) || '',
  });
  setSubDomainCookie(companySubdomain);
  const existingParameters = new URLSearchParams(suffix.split('?')[1]);

  // Create a new URLSearchParams object for the final query parameters
  const queryParams = new URLSearchParams();
  existingParameters.forEach((value, key) => {
    // These parameters are received from BE after we have successfully logged in to USL
    // The USL FE gets a final URL from the BE with these parameters
    // Since we already have exchanged the code for the tokens we don't need to pass them again
    if (
      key !== 'code' &&
      key !== 'state' &&
      key !== 'x-usl-ott' &&
      key !== 'ott'
    ) {
      queryParams.append(key, value); // Add only the allowed parameters
    }
  });

  queryParams.append('refreshToken', AuthClient.refreshToken);
  queryParams.append('clientId', AuthClient.clientId);
  queryParams.append(
    'tenancyHash',
    localStorage.getItem(lookupKeys.tenancyHash) || '',
  );
  queryParams.append('isUnifiedLoginEnabled', 'true');

  if (persistence) {
    queryParams.append('persistence', persistence.toString());
  }

  if (Language.getLoginLanguagePersistence()) {
    queryParams.append('lang', Language.getLocale());
  }

  if (msTeamsUrl) {
    queryParams.append('msTeamsReturnUrl', msTeamsUrl);
  }

  if (storedEnvironment) {
    queryParams.append('setEnvironment', storedEnvironment);
  }

  // Construct the clean redirect URL
  const redirectUrl = `https://${companySubdomain}${rootDomain}`;
  const cleanRedirectUrl = new URL(redirectUrl);

  // Attach the query parameters to the cleanRedirectUrl
  cleanRedirectUrl.search = queryParams.toString();

  // Clean current data and redirect to the correct subdomain
  cookieManager.deleteCookie('JWT_COOKIE', currentDomain);
  AuthClient.deleteAuthSession();
  AppLogger.logFeature(
    'oauth2',
    'Redirecting to company subdomain',
    companySubdomain,
  );
  window.location.href = cleanRedirectUrl.toString();
};

const getSubdomainFromAuthMobile = async () => {
  // we need to make an auth/mobile request to get the correct company subdomain and redirect to it

  const authMobileResponse = await waFetch(
    `${getRegionalApiEndpoints().waapi_root}/auth/mobile`,
    {
      method: 'post',
    },
    undefined,
    true,
    false,
    true,
  );
  const company = authMobileResponse.body?.company;
  return company?.wa_subdomain;
};

/**
 * Logs in from an existing JWT token
 * For development purposes we use the query params to set the JWT token before we redirect via handleLoginRedirect
 */
export const loginFromJwt = async () => {
  appLogger.logFeature(
    'oauth2',
    'loginFromJwt: logging in from existing JWT token',
  );
  // Determine which tokens we should use
  // If running on localhost we need to check for query params otherwise we can use the JWT_COOKIE cookie
  const jwtCookie = JSON.parse(cookieManager.getCookie('JWT_COOKIE') as string);
  let effectiveRefreshToken, effectiveClientId;

  if (isOnLocalhost && refreshTokenParam && clientIdParam) {
    effectiveClientId = clientIdParam;
    effectiveRefreshToken = refreshTokenParam;
  }

  if (jwtCookie?.refreshToken && jwtCookie?.clientId) {
    effectiveClientId = jwtCookie.clientId;
    effectiveRefreshToken = jwtCookie.refreshToken;
  }

  // Get a new id toekn from the refresh token and client id
  AuthClient.clientId = effectiveClientId;
  AuthClient.refreshToken = effectiveRefreshToken;
  // Get a new id token
  const idToken = await AuthClient.refreshIdToken();

  appLogger.logFeature('oauth2', 'Refresh token success', idToken);
  await initialiseHermes();
  await initialiseLogin();
};

const authenticateWithOTT = async ott => {
  await AuthClient.register('web', true);
  appLogger.logFeature('oauth2', 'Authorizing with OTT', ott);
  const authResponse = await AuthClient.authorizeWithOTT(ott);

  const { tenancy_hash } = authResponse;
  if (tenancy_hash) {
    localStorage.setItem(lookupKeys.tenancyHash, JSON.stringify(tenancy_hash));
  }

  await defaultAuthenticationCheck();
};

const storeTenancyHash = () => {
  // Attempt to parse the tenancyHash and check that it's an array of strings
  try {
    const tenancyHashArray: string[] = JSON.parse(
      tenancyHashParam.replace(/'/g, '"'),
    );
    if (
      !Array.isArray(tenancyHashArray) ||
      !tenancyHashArray.every(element => typeof element === 'string')
    ) {
      throw new Error('tenancyHash must be an array of strings');
    }
    localStorage.setItem(
      lookupKeys.tenancyHash,
      JSON.stringify(tenancyHashArray),
    );
  } catch (error) {
    AppLogger.error('Failed to parse (incorrect format) tenancyHash:', error);
    throw error;
  }
};
const addListenerForViewportBreakpointsUpdateTrigger = store => {
  let previousFontSize = '16px';

  const viewportCheck = () => {
    const htmlElement = document.getElementsByTagName('html')[0] as HTMLElement;
    const computedFontSize = window
      .getComputedStyle(htmlElement, null)
      .getPropertyValue('font-size');

    if (computedFontSize !== previousFontSize) {
      store.dispatch(calculateResponsiveState(window));
      previousFontSize = computedFontSize;
    }
  };

  setInterval(viewportCheck, 1500);
};

export async function initPreLoginCompany() {
  // app and login subdomain will never have company details
  // so we don't need to make a request.
  if (getSubdomainFromHost() === 'app' || getSubdomainFromHost() === 'login') {
    return CompanyActions.setCompany({});
  }
  try {
    await CompanyActions.setPreLoginCompanyDetails();
  } catch {
    CompanyActions.setCompany({});
    getStore().dispatch(
      showToast({
        type: ToastType.ERROR,
        message: polyglot.t('api_errors.something_wrong'),
      }),
    );
  }
}

/**
 * This function is the entry point for the Hermes application.
 * It is responsible for initializing the application, setting up the Redux store,
 * handling authentication, and rendering the main component.
 * @returns {Promise<void>}
 */
export async function initialiseHermes(): Promise<void> {
  initiliaseWondering();

  if (!isLocalStorageSupported()) {
    (window as Window).location = '/private-browsing';
    return;
  }

  if (window.WAM.ENV.maintenance) {
    (window as Window).location = '/maintenance';
    return;
  }

  // Allow testable flag to be disabled by query string.
  const { enableTestableFeatures = testableFeaturesEnabled() } =
    queryString.parse(location.search);

  setTestableFlag(JSON.parse(enableTestableFeatures as string));

  const store = initializeStore();
  store.runSaga(rootSagas);

  // Register state listeners
  createSubscription(
    [
      'session.user.anon_user_id',
      'session.user.birthday_date',
      'session.user.gender',
      'session.user.account_type',
      'session.user.originator',
      'session.user.role',
    ],
    GoogleAnalytics.setUser,
  )(store);

  createSubscription(
    ['company.company_id', 'company.name', 'company.demo', 'company.internal'],
    GoogleAnalytics.setCompanyProperties,
  )(store);

  localStorage.setItem(storageKeys.authenticationMethod, 'oauth2');

  await Language.set();
  runOnWebWorker(initPreLoginCompany);
  Language.loadTranslations(Language.getLocale(), true);

  //set interval check for updating viewport break points on text-only zoom
  addListenerForViewportBreakpointsUpdateTrigger(store);
  renderHermes();
}

/**
 * If logged in, checks if on the correct subdomain and redirects if not
 * If not logged in, calls initialiseHermes to set up the app
 * @param attemptRedirect For local development we make redirection to a subdomain optional
 * @returns {Promise<void>}
 *
 */
const defaultAuthenticationCheck = async (attemptRedirect = true) => {
  try {
    const idToken = await AuthClient.checkIfUserIsAuthenticated();

    if (stayOnGenericSubdomain) {
      attemptRedirect = false;
    }

    if (idToken) {
      if (!attemptRedirect) {
        await initialiseHermes();
        await initialiseLogin();
      } else {
        // Make a call to auth mobile to check if we are on the correct subdomain
        const companySubdomain = await getSubdomainFromAuthMobile();
        const subdomain = getSubdomainFromHost();

        // If we aren't on the right company subdomain we need to redirect
        if (companySubdomain && subdomain !== companySubdomain) {
          if (isOnLocalhost) {
            window.confirm('Wrong domain detected - Redirect Now?');
          }
          DatadogClient.logMessage(
            `Wrong domain detected (${subdomain})- Redirecting to ${companySubdomain}`,
          );
          await handleLoginRedirect(companySubdomain);
        } else {
          // If we are on the right subdomain we can login
          // We don't need to keep the cookies as we have the tokens in localstorage
          await initialiseHermes();
          await initialiseLogin();
        }
      }
    } else {
      initialiseHermes();
    }
  } catch (error) {
    AppLogger.error('Error in checkIfUserIsAuthenticated', error);
  }
};

function determineLoginFlow() {
  let flow: string = 'DEFAULT';
  if (tenancyHashParam) {
    storeTenancyHash();
  }

  let isRefreshTokenPresent = false;

  if (!stayOnGenericSubdomain && refreshTokenParam && clientIdParam) {
    isRefreshTokenPresent = true;
  }

  if (isOnGenericSubdomain && getSubdomainCookie() && !stayOnGenericSubdomain) {
    flow = 'WAM_SUBDOMAIN_PRESENT';
  } else if (uslOttPresent) {
    flow = 'USL_OTT_PRESENT';
  } else if (userToken && urlParams['ott']) {
    flow = 'USER_TOKEN_AND_OTT_PRESENT';
  } else if (jwtCookie) {
    flow = 'JWT_COOKIE_PRESENT';
  } else if (isRefreshTokenPresent) {
    flow = 'JWT_PARAMS_PRESENT';
  }
  appLogger.logFeature('oauth2', 'Flow is', flow);
  return flow;
}

/**
 * Determines which USL login method to use
 * Each case will eventually call initialiseHermes along with initialiseLogin if we have existing tokens
 * case JWT_COOKIE_PRESENT: We have previously logged in and there is a JWT_COOKIE, or we are on localhost and have the query params: clientId, idToken and refreshToken
 * case USL_OTT_PRESENT: We have a OTT. This is the urlParams['x-usl-ott'] SSOX approach, currently only used from Zeus to Hermes
 * case USER_TOKEN_AND_OTT_PRESENT: We have a userToken and ott in the urlParams (used by natives to authenticate webviews)
 * case WAM_SUBDOMAIN_PRESENT: Handle existing WAM_SUBDOMAIN cookie and redirect to the the stored subdomain if it exists
 * case DEFAULT: In the Default case we need to call checkIfUserIsAuthenticated
 *
 * @returns {Promise<void>}
 */
export const runHermesWithUSL = async (): Promise<void> => {
  if (AuthClient.clientId) {
    DatadogClient.setGlobalContext({
      public_client_id: AuthClient.clientId,
    });
  }

  appLogger.logFeature('oauth2', 'running Hermes with USL');

  AuthEvents.subscribe('*', async (event, data) => {
    appLogger.logFeature('oauth2', `Received event ${event}`, data);
  });

  const flow = determineLoginFlow();

  switch (flow) {
    case 'WAM_SUBDOMAIN_PRESENT':
      if (isOnLocalhost) {
        // If the developer wants to redirect then let them
        if (window.confirm('Company subdomain cookie present- Redirect Now?')) {
          await handleSubdomainRedirect();
        } else {
          // otherwise just check if we are authenticated
          await defaultAuthenticationCheck(false);
        }
      } else {
        // Redirect without asking
        DatadogClient.logMessage(`FE Flow determined as: ${flow}`);
        await handleSubdomainRedirect();
      }
      break;

    case 'USL_OTT_PRESENT':
      DatadogClient.logMessage(
        `FE Flow determined as: ${flow}, ott: ${uslOTT}`,
      );
      authenticateWithOTT(uslOTT);
      break;

    case 'USER_TOKEN_AND_OTT_PRESENT':
      DatadogClient.logMessage(
        `FE Flow determined as: ${flow}, userToken: ${userToken}`,
      );
      authenticateWithOTT(userToken);
      break;

    case 'JWT_COOKIE_PRESENT': // A JWT_COOKIE is present from a previous login
      // The JWT_COOKIE is set when during the redirect from a generic subdomain
      DatadogClient.logMessage(
        `FE Flow determined as: ${flow}, logging in from JWT_COOKIE`,
      );
      loginFromJwt();
      break;

    case 'JWT_PARAMS_PRESENT': // We are on localhost and have the query params: refreshToken, clientId, tenancyHash
      loginFromJwt();
      break;

    case 'DEFAULT':
    default: {
      await defaultAuthenticationCheck();
      break;
    }
  }
};
